import { Component, computed, DestroyRef, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { DateUtils } from '@/shared/utils/date-utils';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { TabContainer, TabContent } from '@/shared/components/layout/tab-container/tab-container';
import { ProtocolosPrestador } from '@/features/registro/prestadores/dashboard-prestadores/components/protocolos-prestador/protocolos-prestador';
import { InformacoesFinanceirasPrestador } from '@/features/registro/prestadores/dashboard-prestadores/components/informacoes-financeiras-prestador/informacoes-financeiras-prestador';
import { CorrespondenciasPrestador } from '@/features/registro/prestadores/dashboard-prestadores/components/correspondencias-prestador/correspondencias-prestador';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { PrestadorHistoricoInscricao, PrestadorHistoricoInscricaoSalvarRequest, PrestadorHistoricoOpcao } from '@/core/api/sicom-api/registro/prestador/prestador-historico.model';
import { PrestadorHistoricoService } from '@/core/api/sicom-api/registro/prestador/prestador-historico.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type HistoricoTabelaRow = PrestadorHistoricoInscricao & {
    source: PrestadorHistoricoInscricao;
};

@Component({
    selector: 'app-geridas-prestador',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        BotaoAcoes,
        Button,
        CanDirective,
        CardContent,
        ConfirmDialog,
        CorrespondenciasPrestador,
        CustomTableComponent,
        Dialog,
        InformacoesFinanceirasPrestador,
        InputText,
        ProtocolosPrestador,
        ReactiveFormsModule,
        Select,
        TabContainer,
        TabContent
    ],
    providers: [ConfirmationService],
    templateUrl: './geridas-prestador.html',
    styleUrl: './geridas-prestador.scss'
})
export class GeridasPrestador {
    pessoa = input<PessoaDomain>();

    // Componente renderizado sob o slug "historico" no catálogo de permissões (não "geridas").
    protected readonly roleHistoricoEditar = PERMISSOES.prestadores.historico.editar;
    protected readonly EInconPos = EInconPos;

    get itensAcoes(): AppMenuItem[] {
        return [
            acaoMenuItem({
                id: 'atualizar-historico',
                label: 'Atualizar histórico',
                icon: 'pi pi-pencil',
                cor: 'editar',
                disabled: this.selected !== 'INSCRICOES',
                permission: this.roleHistoricoEditar,
                command: () => this.abrirAtualizacao()
            }),
            acaoMenuItem({ id: 'recarregar', label: 'Recarregar', icon: 'pi pi-refresh', cor: 'info', disabled: this.selected !== 'INSCRICOES', command: () => this.carregar() })
        ];
    }

    selected = 'INSCRICOES';
    readonly options = [
        { label: 'Inscrições', value: 'INSCRICOES' },
        { label: 'Protocolos', value: 'PROTOCOLOS' },
        { label: 'Informações Financeiras', value: 'FINANCEIRO' },
        { label: 'Correspondências', value: 'CORRESPONDENCIAS' }
    ];

    private readonly destroyRef = inject(DestroyRef);
    private readonly formBuilder = inject(FormBuilder);
    private readonly service = inject(PrestadorHistoricoService);
    private readonly confirmation = inject(ConfirmationService);

    readonly linhas = signal<PrestadorHistoricoInscricao[]>([]);
    readonly first = signal(0);
    readonly rows = signal(10);
    readonly totalRecords = signal(0);
    readonly linhasTabela = computed<HistoricoTabelaRow[]>(() =>
        this.linhas().map((item) => ({
            ...item,
            dataSituacao: DateUtils.formatDate(item.dataSituacao),
            source: item
        }))
    );
    // Coloração da linha (espelha o legado DBGrid3DrawColumnCell): verde = inscrição atual;
    // vermelho = inscrições anteriores (histórico).
    readonly rowStyleHistorico = (row: { situacaoAtual?: boolean }): Record<string, string> => ({
        color: row.situacaoAtual ? '#16a34a' : '#dc2626'
    });
    readonly loading = signal(false);
    readonly dialogVisivel = signal(false);
    readonly salvando = signal(false);
    readonly carregandoOpcoes = signal(false);
    readonly tiposInscricao = signal<PrestadorHistoricoOpcao[]>([]);
    readonly situacoes = signal<PrestadorHistoricoOpcao[]>([]);
    readonly registroEmEdicao = signal<PrestadorHistoricoInscricao | null>(null);
    readonly formulario = this.formBuilder.group({
        idTipoInscricao: this.formBuilder.control<number | null>(null, Validators.required),
        idSituacao: this.formBuilder.control<number | null>(null, Validators.required),
        dataSituacao: this.formBuilder.control('', Validators.required)
    });

    readonly metadata: TableMetadata = {
        headers: {
            tipoInscricao: { type: 'text', value: 'Tipo Inscrição', default: '-', hidden: false },
            situacao: { type: 'text', value: 'Situação', default: '-', hidden: false },
            dataSituacao: { type: 'text', value: 'Data da Situação', default: '-', hidden: false }
        },
        title: 'situacao',
        actions: {
            editar: { label: 'Editar', icon: 'pi pi-pencil', color: 'secondary', command: (item: HistoricoTabelaRow) => this.editarRegistro(item.source), permission: PERMISSOES.prestadores.historico.editar },
            remover: { label: 'Remover', icon: 'pi pi-trash', color: 'danger', command: (item: HistoricoTabelaRow) => this.removerRegistro(item.source), permission: PERMISSOES.prestadores.historico.editar }
        },
        defaultSortField: 'dataSituacao',
        defaultSortOrder: -1
    };

    constructor() {
        effect(() => {
            const p = this.pessoa();
            if (p?.id && p.uf) {
                untracked(() => this.carregar());
            } else {
                this.linhas.set([]);
            }
        });
    }

    carregar(): void {
        const p = this.pessoa();
        if (!p?.id || !p.uf) return;

        this.loading.set(true);
        this.service
            .listarInscricoes(p.id, p.uf, Math.floor(this.first() / this.rows()), this.rows())
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.loading.set(false))
            )
            .subscribe({
                next: (response) => {
                    this.linhas.set(response.content ?? []);
                    this.totalRecords.set(response.totalElements ?? 0);
                },
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => {
                    this.linhas.set([]);
                    this.totalRecords.set(0);
                }
            });
    }

    onPaginaAlterada(event: PaginatorState): void {
        this.first.set(event.first ?? 0);
        this.rows.set(event.rows ?? 10);
        this.carregar();
    }

    abrirAtualizacao(): void {
        if (!this.pessoa()?.id) return;

        this.novoRegistro();
        this.dialogVisivel.set(true);
        this.carregarTiposInscricao();
    }

    aoAlterarVisibilidadeDialog(visivel: boolean): void {
        this.dialogVisivel.set(visivel);
        if (!visivel) {
            this.novoRegistro();
            this.carregar();
        }
    }

    novoRegistro(): void {
        this.registroEmEdicao.set(null);
        this.formulario.reset();
        this.situacoes.set([]);
    }

    editarRegistro(registro: PrestadorHistoricoInscricao): void {
        this.registroEmEdicao.set(registro);
        this.formulario.setValue({
            idTipoInscricao: registro.idTipoInscricao,
            idSituacao: registro.idSituacao,
            dataSituacao: registro.dataSituacao
        });
        this.dialogVisivel.set(true);
        this.carregarTiposInscricao();
        this.carregarSituacoes(registro.idTipoInscricao, false);
    }

    aoAlterarTipoInscricao(): void {
        const idTipoInscricao = this.formulario.controls.idTipoInscricao.value;
        this.formulario.controls.idSituacao.setValue(null);
        this.situacoes.set([]);

        if (idTipoInscricao) {
            this.carregarSituacoes(idTipoInscricao);
        }
    }

    salvarRegistro(): void {
        const pessoa = this.pessoa();
        const valores = this.formulario.getRawValue();
        if (!pessoa?.id || !pessoa.uf || this.formulario.invalid || valores.idTipoInscricao === null || valores.idSituacao === null || valores.dataSituacao === null) {
            return;
        }

        const request: PrestadorHistoricoInscricaoSalvarRequest = {
            id: pessoa.id,
            uf: pessoa.uf,
            idTipoInscricao: valores.idTipoInscricao,
            idSituacao: valores.idSituacao,
            dataSituacao: valores.dataSituacao
        };
        const registroOriginal = this.registroEmEdicao();
        const requisicao = registroOriginal
            ? this.service.atualizarInscricao({
                  ...request,
                  idTipoInscricaoOriginal: registroOriginal.idTipoInscricao,
                  idSituacaoOriginal: registroOriginal.idSituacao,
                  dataSituacaoOriginal: registroOriginal.dataSituacao
              })
            : this.service.criarInscricao(request);

        this.salvando.set(true);
        requisicao
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.salvando.set(false))
            )
            .subscribe(() => {
                this.novoRegistro();
                this.carregar();
            });
    }

    removerRegistro(registro: PrestadorHistoricoInscricao): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) return;

        this.confirmation.confirm({
            message: `Remover histórico ${registro.tipoInscricao} - ${registro.situacao} de ${registro.dataSituacao}?`,
            accept: () =>
                this.service
                    .removerInscricao({
                        id: pessoa.id,
                        uf: pessoa.uf,
                        idTipoInscricao: registro.idTipoInscricao,
                        idSituacao: registro.idSituacao,
                        dataSituacao: registro.dataSituacao
                    })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        this.novoRegistro();
                        this.carregar();
                    })
        });
    }

    private carregarTiposInscricao(): void {
        if (this.tiposInscricao().length > 0) return;

        this.carregandoOpcoes.set(true);
        this.service
            .listarTiposInscricao()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregandoOpcoes.set(false))
            )
            .subscribe((tipos) => this.tiposInscricao.set(tipos));
    }

    private carregarSituacoes(idTipoInscricao: number, limparSelecao = true): void {
        this.carregandoOpcoes.set(true);
        this.service
            .listarSituacoes(idTipoInscricao)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregandoOpcoes.set(false))
            )
            .subscribe((situacoes) => {
                this.situacoes.set(situacoes);
                if (limparSelecao) {
                    this.formulario.controls.idSituacao.setValue(null);
                }
            });
    }
}
